import { ActionType } from './command-parser.model';

export interface TokenizeResult {
  tokens: string[];
  extracted: { phrase: string; replacement: string }[];
  /** If a verb+object removal pattern was detected, this is set before keyword matching */
  detectedRemoveIntent: ActionType | null;
}

// --- Step 1: Normalize input ---

const STOPWORDS = new Set([
  'il', 'lo', 'la', 'le', 'li', 'gli', 'i',
  'un', 'uno', 'una',
  'del', 'della', 'dello', 'delle', 'degli', 'dei',
  'al', 'allo', 'alla', 'alle', 'agli', 'ai',
  'di', 'da',
]);

const SYNONYMS: [string, string][] = [
  ['pasto', 'pasta'],
];

/** Light normalization: lowercase, trim, apostrophes, collapse whitespace. No stopword removal. */
export function normalizeLightInput(input: string): string {
  let text = input.toLowerCase().trim();

  // Normalize apostrophes: "l'assenza" → "l assenza", "dell'evento" → "dell evento"
  text = text.replace(/[''`]/g, ' ');

  // Collapse whitespace
  text = text.replace(/\s+/g, ' ').trim();

  return text;
}

function normalizeInput(input: string): string {
  let text = normalizeLightInput(input);

  // Remove stopwords (articles, prepositions)
  const words = text.split(' ');
  const filtered = words.filter(w => !STOPWORDS.has(w));
  text = filtered.join(' ');

  // Apply synonyms
  for (const [from, to] of SYNONYMS) {
    // Replace standalone word only (not inside a longer word)
    text = text.replace(new RegExp(`\\b${from}\\b`, 'g'), to);
  }

  return text.trim();
}

// --- Step 2: Verb+Object pattern matcher for removal ---

const REMOVE_VERBS = new Set([
  'rimuovi', 'cancella', 'togli', 'annulla', 'elimina',
]);

const OBJECT_PATTERNS: { pattern: string; intentGroup: 'assenza' | 'pasta' }[] = [
  // Longer patterns first (greedy match)
  { pattern: 'pasta in bianco', intentGroup: 'pasta' },
  { pattern: 'pasta bianco', intentGroup: 'pasta' },
  { pattern: 'pasta bianca', intentGroup: 'pasta' },
  { pattern: 'assenza', intentGroup: 'assenza' },
  { pattern: 'pasta', intentGroup: 'pasta' },
  { pattern: 'bianco', intentGroup: 'pasta' },
  { pattern: 'bianca', intentGroup: 'pasta' },
];

const REMOVE_INTENT_MAP: Record<string, ActionType> = {
  'assenza': 'rimuovi-assenza',
  'pasta': 'rimuovi-pasta-in-bianco',
};

function detectRemoveIntent(normalizedText: string): { intent: ActionType; consumed: string } | null {
  // Check if text starts with a remove verb
  const words = normalizedText.split(' ');
  const firstWord = words[0];

  if (!REMOVE_VERBS.has(firstWord)) return null;

  // Rest of the text after the verb
  const rest = words.slice(1).join(' ');

  // Try to match an object pattern in the rest
  for (const { pattern, intentGroup } of OBJECT_PATTERNS) {
    if (rest === pattern || rest.startsWith(pattern + ' ') || rest.startsWith(pattern)) {
      return {
        intent: REMOVE_INTENT_MAP[intentGroup],
        consumed: firstWord + ' ' + pattern,
      };
    }
  }

  return null;
}

// --- Step 3: Multi-word placeholder (legacy, for add actions) ---

const MULTI_WORD_PHRASES: { phrase: string; replacement: string }[] = [
  { phrase: 'pasta in bianco', replacement: '__PASTA_IN_BIANCO__' },
  { phrase: 'pasta bianco', replacement: '__PASTA_BIANCO__' },
  { phrase: 'pasta bianca', replacement: '__PASTA_BIANCA__' },
  { phrase: 'non presente', replacement: '__NON_PRESENTE__' },
];

// --- Main tokenize function ---

export function tokenize(input: string): TokenizeResult {
  // Step 1: Normalize
  let normalized = normalizeInput(input);

  const extracted: { phrase: string; replacement: string }[] = [];
  let detectedRemoveIntent: ActionType | null = null;

  // Step 2: Detect verb+object removal pattern
  const removeMatch = detectRemoveIntent(normalized);
  if (removeMatch) {
    detectedRemoveIntent = removeMatch.intent;
    // Replace the consumed portion with a placeholder
    normalized = normalized.replace(removeMatch.consumed, `__REMOVE_${removeMatch.intent.toUpperCase().replace(/-/g, '_')}__`);
  }

  // Step 3: Multi-word placeholder (for add actions)
  if (!detectedRemoveIntent) {
    for (const mw of MULTI_WORD_PHRASES) {
      if (normalized.includes(mw.phrase)) {
        normalized = normalized.replace(mw.phrase, mw.replacement);
        extracted.push(mw);
      }
    }
  }

  const tokens = normalized.split(' ').filter(t => t.length > 0);

  return { tokens, extracted, detectedRemoveIntent };
}
